import { useRouteLoaderData } from '@remix-run/react'
import { useSanityImages } from '~/hooks'
import { parsePostalAddress, portableTextToPlain } from '~/utils/format'
import type { ListingSchemaProduct, RootDataProps } from '~/types'

const siteUrl = 'https://www.hss.com'

export type SchemaType =
  | 'website'
  | 'store'
  | 'page'
  | 'breadcrumbs'
  | 'category'
  | 'product'
  | 'blogPost'

/*
-----------------------------
  Helpers
-----------------------------
*/

const absUrl = (urlOrPath?: string | null) => {
  if (!urlOrPath) return siteUrl
  try {
    return new URL(urlOrPath, siteUrl).toString()
  } catch {
    return siteUrl
  }
}

const stripHtml = (html?: string | null) =>
  html ? html.replace(/<[^>]+>/g, '') : ''

/*
-----------------------------
  WebPage Schema Builder
-----------------------------
*/

export const getWebPageSchema = (page: {
  metaTitle: string
  metaDescription: string
  redirect?: string | null
}) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: page?.metaTitle ?? '',
    description: page?.metaDescription ?? '',
    url: absUrl(page?.redirect),
    inLanguage: 'en-GB',
    isPartOf: {
      '@type': 'WebSite',
      name: 'HSS',
      url: siteUrl,
    },
  }
}

/*
-----------------------------
  WebSite Schema Builder
-----------------------------
*/

export const getWebSiteSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'HSS DIY',
    url: siteUrl,
    description:
      'HSS Hire is the UK’s leading tool and equipment hire company, offering a wide range of tools for hire including access, cleaning, construction, gardening, and more.',
    inLanguage: 'en-GB',
    publisher: {
      '@id': `${siteUrl}/#organization`,
    },
  }
}

/*
-----------------------------
  Store / Organization Schema Builder
  (choose Store if this is ecom)
-----------------------------
*/

export const getStoreSchema = (options?: {
  logoUrl?: string | null
  sameAs?: string[]
  phone?: string | null
  email?: string | null
  companyNo?: string | null
  address?: string | null
}) => {
  const logoUrl = options?.logoUrl ?? undefined
  /*
    The CMS field carries display text such as "Company No. 11084154", so pull
    the number out rather than validating the whole string. UK numbers are 8
    characters, and a Companies House URL built from a malformed one would
    point at the wrong company, so no match means no identifier.
  */
  const companyNo =
    options?.companyNo
      ?.match(/\b([A-Z]{2}\d{6}|\d{8})\b/i)?.[0]
      .toUpperCase() ?? null
  const postalAddress = parsePostalAddress(options?.address)

  /*
    Companies House is the authoritative corroborator for a UK company, so the
    record is derived from the number rather than listed by hand. Only confirmed
    profiles belong in sameAs, a wrong entry asserts the wrong entity.
  */
  const sameAs = [
    ...(options?.sameAs ?? []),
    companyNo &&
      `https://find-and-update.company-information.service.gov.uk/company/${companyNo}`,
  ].filter(Boolean) as string[]

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: 'HSS DIY',
    legalName: 'HSS ProService Ltd',
    description:
      'HSS Hire is a UK tool and equipment hire company, offering hire and sale of access, cleaning, construction, gardening and power equipment nationwide.',
    url: siteUrl,
    areaServed: {
      '@type': 'Country',
      name: 'United Kingdom',
    },
    ...(logoUrl && {
      logo: {
        '@type': 'ImageObject',
        url: logoUrl,
      },
    }),
    ...(companyNo && {
      identifier: {
        '@type': 'PropertyValue',
        name: 'Companies House',
        value: companyNo,
      },
    }),
    ...(options?.address && {
      address: postalAddress
        ? { '@type': 'PostalAddress', ...postalAddress }
        : options.address,
    }),
    ...(sameAs.length > 0 && { sameAs }),
    ...((options?.phone || options?.email) && {
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        areaServed: 'GB',
        availableLanguage: 'English',
        ...(options.phone && { telephone: options.phone }),
        ...(options.email && { email: options.email }),
      },
    }),
  }
}

/* 
-----------------------------
  BreadcrumbList Schema Builder
  Useful for navigation paths (e.g. Home > Collection > Product).
  Can be conditionally included alongside other schemas.
----------------------------- 
*/

export const getBreadcrumbSchema = (
  crumbs: Array<{ name: string; url: string }>
) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: absUrl(c.url),
    })),
  }
}

/*
-----------------------------
  Category / Collection Schema
  schema.org: CollectionPage + ItemList
-----------------------------
*/

export const getCategorySchema = (category: {
  title: string
  description?: string | null
  slug: string
  products?: ListingSchemaProduct[]
  /** (page - 1) * pageSize, so page two numbers positions 10 onward */
  startIndex?: number
}) => {
  const categoryUrl = absUrl(category.slug)

  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: category.title,
    description: stripHtml(category.description),
    url: categoryUrl,
    mainEntity: category.products?.length
      ? {
          '@type': 'ItemList',
          itemListElement: category.products.map((p, i) => ({
            '@type': 'ListItem',
            position: (category.startIndex ?? 0) + i + 1,
            item: {
              '@type': 'Product',
              name: p.title,
              url: absUrl(p.slug),
              ...(p.image && { image: absUrl(p.image) }),
              ...(p.sku && { sku: p.sku }),
              ...(p.price &&
                p.currency && {
                  offers: {
                    '@type': 'Offer',
                    url: absUrl(p.slug),
                    price: p.price,
                    priceCurrency: p.currency,
                  },
                }),
            },
          })),
        }
      : undefined,
  }
}

/*
-----------------------------
  Product Schema
  schema.org: Product + Offer + optional AggregateRating
-----------------------------
*/

// Returns on bought goods, from clause 9.4 of the purchase T&Cs and the
// /returns page: 30 days from delivery, sent back by post, the customer pays
// postage unless the item is damaged, faulty or wrong. Customer-paid postage
// has no fixed amount, so returnFees says the customer covers it rather than
// naming a fee Google would then want priced. Hire has no returns
const buyReturnPolicy = {
  '@type': 'MerchantReturnPolicy',
  applicableCountry: 'GB',
  returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
  merchantReturnDays: 30,
  returnMethod: 'https://schema.org/ReturnByMail',
  returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
  refundType: 'https://schema.org/FullRefund',
  itemDefectReturnFees: 'https://schema.org/FreeReturn',
}

// Delivery on bought goods, from the /returns page: orders placed by 4:30pm
// Monday to Friday arrive within 48 hours. The rate is left out because
// delivery is priced per product and postcode, which isn't known here
const buyShippingDetails = {
  '@type': 'OfferShippingDetails',
  shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'GB' },
  deliveryTime: {
    '@type': 'ShippingDeliveryTime',
    businessDays: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'https://schema.org/Monday',
        'https://schema.org/Tuesday',
        'https://schema.org/Wednesday',
        'https://schema.org/Thursday',
        'https://schema.org/Friday',
      ],
    },
    handlingTime: {
      '@type': 'QuantitativeValue',
      minValue: 0,
      maxValue: 0,
      unitCode: 'DAY',
    },
    transitTime: {
      '@type': 'QuantitativeValue',
      minValue: 1,
      maxValue: 2,
      unitCode: 'DAY',
    },
  },
}

export const getProductSchema = (product: {
  title: string
  description?: string | null
  slug: string
  sku?: string | null
  brandName?: string | null
  images?: string[]
  price?: number | string | null
  highPrice?: number | string | null
  offerCount?: number | null
  currency?: string | null
  availability?: string | null
  ratingAverage?: number | null
  ratingCount?: number | null
  fromPricing?: boolean
  category?: string | null
  properties?: { name: string; value: string }[]
}) => {
  const productUrl = absUrl(product.slug)

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    description: stripHtml(product.description),
    url: productUrl,
    image: product.images?.map(absUrl),
    sku: product.sku ?? undefined,
    category: product.category ?? undefined,
    brand: product.brandName
      ? { '@type': 'Brand', name: product.brandName }
      : undefined,

    additionalProperty:
      product.properties && product.properties.length > 0
        ? product.properties.map(p => ({
            '@type': 'PropertyValue',
            name: p.name,
            value: p.value,
          }))
        : undefined,

    // No VideoObject: Google requires thumbnailUrl and uploadDate, and the
    // product data only has the YouTube link, so a video entry would be invalid

    aggregateRating:
      product.ratingAverage != null && product.ratingCount != null
        ? {
            '@type': 'AggregateRating',
            ratingValue: product.ratingAverage.toString(),
            reviewCount: product.ratingCount.toString(),
          }
        : undefined,

    offers:
      product.price == null || !product.currency
        ? undefined
        : product.fromPricing
          ? {
              // Hire is from pricing, the total depends on the date range
              // picked, so a fixed Offer price would misrepresent it
              '@type': 'AggregateOffer',
              url: productUrl,
              priceCurrency: product.currency,
              lowPrice: product.price,
              // Google flags a range without these, so a single variant is a
              // range of one: same low and high price, one offer
              highPrice: product.highPrice ?? product.price,
              offerCount: product.offerCount ?? 1,
              businessFunction: 'http://purl.org/goodrelations/v1#LeaseOut',
              availability: product.availability
                ? `https://schema.org/${product.availability}`
                : undefined,
            }
          : {
              '@type': 'Offer',
              url: productUrl,
              priceCurrency: product.currency,
              price: product.price,
              availability: product.availability
                ? `https://schema.org/${product.availability}`
                : undefined,
              itemCondition: 'https://schema.org/NewCondition',
              shippingDetails: buyShippingDetails,
              hasMerchantReturnPolicy: buyReturnPolicy,
            },
  }
}

export type ProductSchemaInput = Parameters<typeof getProductSchema>[0]

// Google rejects a Product with none of offers, review or aggregateRating, so an
// unpriced, unrated product (price on application) carries no Product entry
export const hasValidProductSchema = (product: ProductSchemaInput): boolean =>
  (product.price != null && !!product.currency) ||
  (product.ratingAverage != null && product.ratingCount != null)

export const buildProductSchemaInput = (
  product: any,
  path?: string | null,
  seo?: { redirect?: string | null; metaDescription?: string | null } | null
): ProductSchemaInput => {
  const primaryVariant = product?.variants?.[0]

  // Hire is priced from a daily rate that grows with the hire period
  const fromPricing = product?.hyraxProductType === 'bulk'

  // Every variant price, so multi-variant products can express a range
  const variantAmounts: number[] = (product?.variants ?? [])
    .map((v: any) => v?.salePrice?.amount ?? v?.guidePrice?.amount)
    .filter((a: unknown): a is number => typeof a === 'number')

  const rawAmount =
    product?.salePrice?.amount ??
    product?.guidePrice?.amount ??
    (variantAmounts.length > 0 ? Math.min(...variantAmounts) : undefined) ??
    primaryVariant?.salePrice?.amount ??
    primaryVariant?.guidePrice?.amount

  const price =
    typeof rawAmount === 'number' ? rawAmount.toFixed(2) : (rawAmount ?? null)

  const highAmount =
    variantAmounts.length > 1 ? Math.max(...variantAmounts) : undefined

  const highPrice =
    typeof highAmount === 'number' && highAmount.toFixed(2) !== price
      ? highAmount.toFixed(2)
      : null

  const currency =
    product?.salePrice?.currency ??
    product?.guidePrice?.currency ??
    primaryVariant?.salePrice?.currency ??
    primaryVariant?.guidePrice?.currency ??
    'GBP'

  // Hire and buy products hold images on the variant, not the product
  const imageSource =
    product?.images ??
    primaryVariant?.images ??
    (product?.image ? [product.image] : [])

  const images = imageSource.map((i: any) => i?.imageUrl).filter(Boolean)

  // Specs, bullets and safety notes, carried in JSON-LD now the microdata has gone
  const properties = [
    ...(primaryVariant?.specifications ?? []).map((e: any) => ({
      name: e?.label ?? '',
      value: `${e?.value ?? ''}${e?.unit ?? ''}`,
    })),
    ...(primaryVariant?.detailsBulletPoints ?? []).map((e: string) => ({
      name: 'feature',
      value: e,
    })),
    ...(primaryVariant?.safetyIcons ?? []).map((e: string) => ({
      name: 'safety',
      value: String(e),
    })),
  ].filter(e => e.name && e.value)

  return {
    title: product?.name ?? '',
    // Some products have a blank description, so fall back to the shorter
    // copy, then the meta description, rather than leave it empty
    description:
      product?.description ||
      product?.shortDescription ||
      seo?.metaDescription ||
      '',
    slug: path ?? product?.slug ?? seo?.redirect ?? '',
    sku: product?.variantStockCode ?? product?.stockCode ?? undefined,
    brandName:
      product?.brand?.name ??
      product?.vendor?.name ??
      primaryVariant?.stringAttributes?.find((a: any) => a.name === 'brand')
        ?.value ??
      undefined,
    images: images.length > 0 ? images : undefined,
    price,
    highPrice,
    offerCount: variantAmounts.length > 1 ? variantAmounts.length : null,
    currency,
    category:
      product?.stringAttributes?.find((a: any) => a.name === 'categories')
        ?.value ?? null,
    // HSS have an always-available clause, so every offer is in stock even
    // though live stock isn't resolved until the client fetches charges
    availability: 'InStock',
    fromPricing,
    properties,
  }
}

/*
-----------------------------
  Blog Post Schema
  schema.org: BlogPosting
-----------------------------
*/

export const getBlogPostSchema = (post: {
  title: string
  description?: string | null
  slug: string
  author?: string | null
  publishedAt?: string | null
  updatedAt?: string | null
  image?: string | null
}) => {
  const postUrl = absUrl(post.slug)

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description ?? undefined,
    url: postUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': postUrl,
    },
    author: post.author ? { '@type': 'Person', name: post.author } : undefined,
    datePublished: post.publishedAt ?? undefined,
    dateModified: post.updatedAt ?? undefined,
    image: post.image
      ? {
          '@type': 'ImageObject',
          url: absUrl(post.image),
        }
      : undefined,
  }
}

/*
-----------------------------
  FAQPage Schema
-----------------------------
*/

export const getFaqSchema = (
  faqs: Array<{ question: string; answer: string }>
) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(e => ({
    '@type': 'Question',
    name: e.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: stripHtml(e.answer),
    },
  })),
})

// FAQ blocks on a CMS page become its FAQPage entry. FAQ List blocks hold
// their Q&As in block data, the FAQs block lists those filed under its topics.
// A page carries one FAQPage, so both feed it and each question appears once
export const getFaqBlockSchema = (
  blocks?: {
    _type?: string
    data?: {
      items?: { question?: string; answer?: unknown }[]
      faqs?:
        | {
            question?: string
            answer?: unknown
            categories?: { _ref: string }[]
          }[]
        | null
      categories?: ({ id: string } | null)[] | null
    }
  }[]
) => {
  const items = (blocks ?? []).flatMap(block => {
    if (block?._type === 'faqList') return block.data?.items ?? []

    if (block?._type === 'faqsBlock') {
      const topicIds = (block.data?.categories ?? []).map(e => e?.id)

      // Only the questions the block shows, ie those under one of its topics
      return (block.data?.faqs ?? []).filter(faq =>
        faq?.categories?.some(e => topicIds.includes(e._ref))
      )
    }

    return []
  })

  // A question filed under two topics, or in two blocks, is listed once
  const faqs = [
    ...new Map(
      items.filter(e => e?.question).map(e => [e.question, e])
    ).values(),
  ].map(e => ({
    question: e.question ?? '',
    answer: portableTextToPlain(e.answer),
  }))

  return faqs.length ? getFaqSchema(faqs) : null
}

/* 
-----------------------------
  Registry + Resolver
  Registry maps schema types → builder functions.
  Resolver picks the right builder and injects region automatically.
----------------------------- 
*/

const schemaRegistry = {
  website: () => getWebSiteSchema(),

  store: ({ logoUrl }: Extract<SchemaBuilderArgs, { type: 'store' }>) =>
    getStoreSchema({ logoUrl }),

  page: ({ page }: Extract<SchemaBuilderArgs, { type: 'page' }>) =>
    getWebPageSchema(page),

  breadcrumbs: ({
    crumbs,
  }: Extract<SchemaBuilderArgs, { type: 'breadcrumbs' }>) =>
    getBreadcrumbSchema(crumbs),

  category: ({ category }: Extract<SchemaBuilderArgs, { type: 'category' }>) =>
    getCategorySchema(category),

  product: ({ product }: Extract<SchemaBuilderArgs, { type: 'product' }>) =>
    getProductSchema(product),

  blogPost: ({ post }: Extract<SchemaBuilderArgs, { type: 'blogPost' }>) =>
    getBlogPostSchema(post),

  faq: ({ faqs }: Extract<SchemaBuilderArgs, { type: 'faq' }>) =>
    getFaqSchema(faqs),
} as const

/*
-----------------------------
  Schema Builder Args
  Discriminated union keyed by `type`.
  Each schema type enforces its own input shape.
-----------------------------
*/

export type SchemaBuilderArgs =
  | {
      type: 'website'
      logoUrl?: string
    }
  | {
      type: 'store'
      logoUrl?: string
    }
  | {
      type: 'page'
      page: {
        metaTitle: string
        metaDescription: string
        redirect?: string | null
      }
    }
  | {
      type: 'breadcrumbs'
      crumbs: Array<{ name: string; url: string }>
    }
  | {
      type: 'category'
      category: {
        title: string
        description?: string | null
        slug: string
        products?: ListingSchemaProduct[]
        startIndex?: number
      }
    }
  | {
      type: 'product'
      product: ProductSchemaInput
    }
  | {
      type: 'faq'
      faqs: Array<{ question: string; answer: string }>
    }
  | {
      type: 'blogPost'
      post: {
        title: string
        description?: string | null
        slug: string
        author?: string | null
        publishedAt?: string | null
        updatedAt?: string | null
        image?: string | null
      }
    }

/*
-----------------------------
  getSchema Resolver
  Looks up the correct builder from the registry.
-----------------------------
*/

export const getSchema = <T extends SchemaBuilderArgs>(
  args: T
): Record<string, unknown> => {
  const builder = schemaRegistry[args.type] as (
    a: any
  ) => Record<string, unknown>

  return builder(args)
}

/* 
-----------------------------
  Global Schema Wrapper
  Always prepends Store + WebSite schemas.
  Accepts either a single schema or an array.
----------------------------- 
*/

const globalSchemas = (options?: {
  logoUrl?: string | null
  sameAs?: string[]
  phone?: string | null
  email?: string | null
  companyNo?: string | null
  address?: string | null
}) => [getWebSiteSchema(), getStoreSchema(options)]

export const Schema = ({
  data,
  includeGlobal = true,
}: {
  data: Record<string, unknown> | Record<string, unknown>[]
  includeGlobal?: boolean
}) => {
  const specificSchemas = Array.isArray(data) ? data : [data]

  // Grab site data from root loader
  const rootData = useRouteLoaderData('root') as RootDataProps | undefined
  const { urlFor } = useSanityImages()

  const logoImage = rootData?.site?.global?.logo
  const logoUrl = logoImage ? urlFor(logoImage).auto('format').url() : undefined

  // Social profiles double as sameAs, corroborating the entity externally
  const global = rootData?.site?.global
  const sameAs = global?.socialChannels?.map(e => e.link).filter(Boolean) ?? []

  const allSchemas = includeGlobal
    ? [
        ...globalSchemas({
          logoUrl,
          sameAs,
          phone: global?.phoneNumber,
          email: global?.emailAddress,
          companyNo: global?.companyNo,
          address: global?.address,
        }),
        ...specificSchemas,
      ]
    : specificSchemas

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(allSchemas) }}
    />
  )
}
